import { InfoCircleOutlined } from '@ant-design/icons'
import {
  ArrowLeftIcon,
  Button as DsButton,
  Drawer as DsDrawer
} from '@lmc/design-system'
import { theme } from '@lmc/ui-kit'
import { Spinner } from '@lmc/ui-kit/src/feedback/Spinner'
import { Button, Text } from '@lmc/ui-kit/src/general'
import type { BodyMapPickerEntry } from '@lmc/validations'
import { Drawer } from 'antd'
import { hideIntercom, showIntercom } from 'common/services/intercom'
import useBodyMapPickerList from 'hooks/useBodyMapPickerList'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import BodyMapGroup from './BodyMapGroup'
import BodyMapItem from './BodyMapItem'

const DRAWER_WIDTH = 736

/**
 * The design shows 15 body maps at a time, revealed client-side — the endpoint
 * returns one capped payload (see useBodyMapPickerList). Counted in body maps,
 * not rows, but a group is never split: a page can run a few maps past the
 * target when a group straddles the boundary.
 */
const BODY_MAPS_PAGE_SIZE = 15

export type AddBodyMapsDrawerProps = {
  /**
   * Opened from a v2 step drawer: the design system drawer, with a Back
   * button before the title. Off, it is the v1 form's antd drawer, unchanged.
   */
  hasBackLink?: boolean
  isOpen: boolean
  onAdd: (reportIds: string[]) => void
  onClose: () => void
  residentId?: string
  selectedIds: string[]
  testId?: string
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
    box-shadow: none;
  }

  /* antd puts the close button before the title; the design has it on the right. */
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  .ant-drawer-title {
    font-size: 24px;
    font-weight: 500;
  }

  .ant-drawer-close {
    margin: 0;
    padding: 4px;
  }

  .ant-drawer-footer {
    border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
    box-shadow: none;
    padding: 24px;
  }

  .ant-drawer-body {
    padding: 16px 24px;
  }
`

const Hint = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  padding-bottom: 12px;

  .anticon {
    font-size: 24px;
  }
`

const Entries = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const Centred = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const ShowMoreRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

/**
 * Selection is local until Add, so Cancel genuinely discards — the form hears
 * about a change only when the user commits one.
 */
const AddBodyMapsDrawer = ({
  hasBackLink = false,
  isOpen,
  onAdd,
  onClose,
  residentId,
  selectedIds,
  testId = 'add-body-maps'
}: AddBodyMapsDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { entries, error, isLoading } = useBodyMapPickerList({
    enabled: isOpen,
    residentId
  })

  const [localSelectedIds, setLocalSelectedIds] =
    useState<string[]>(selectedIds)
  const [expandedReportIds, setExpandedReportIds] = useState<string[]>([])
  const [visibleCount, setVisibleCount] = useState(BODY_MAPS_PAGE_SIZE)

  /**
   * Re-seed on every open. Seeding on mount is not enough: destroyOnClose
   * destroys the Drawer's *children*, and this component wraps the Drawer, so a
   * stale selection survived a close and reopen. Keyed on isOpen alone, since
   * re-running on selectedIds would discard the user's in-panel selection.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync on open only, see above
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIds(selectedIds)
      setExpandedReportIds([])
      setVisibleCount(BODY_MAPS_PAGE_SIZE)
    }
  }, [isOpen])

  /**
   * The Intercom launcher is fixed bottom-right, over this panel's Cancel and Add
   * buttons. Restored unconditionally: gating on Root's INTERCOM_PATHS looks
   * tidier but never fires here, because the case screens are `edit-case` routes
   * that are not on that list yet plainly do show the launcher.
   */
  useEffect(() => {
    if (!isOpen) {
      return
    }

    hideIntercom()

    return () => {
      showIntercom()
    }
  }, [isOpen])

  const toggleOne = (reportId: string) =>
    setLocalSelectedIds((current) =>
      current.includes(reportId)
        ? current.filter((id) => id !== reportId)
        : [...current, reportId]
    )

  const toggleMany = (reportIds: string[], shouldSelect: boolean) =>
    setLocalSelectedIds((current) =>
      shouldSelect
        ? [...current, ...reportIds.filter((id) => !current.includes(id))]
        : current.filter((id) => !reportIds.includes(id))
    )

  const toggleExpanded = (reportId: string) =>
    setExpandedReportIds((current) =>
      current.includes(reportId)
        ? current.filter((id) => id !== reportId)
        : [...current, reportId]
    )

  // Whole entries only, so a straddling group is shown in full rather than cut
  // at the page boundary.
  const visibleEntries: BodyMapPickerEntry[] = []
  let visibleReportCount = 0
  for (const entry of entries) {
    if (visibleReportCount >= visibleCount) {
      break
    }
    visibleEntries.push(entry)
    visibleReportCount += entry.reports.length
  }
  const hasMore = visibleEntries.length < entries.length

  const renderEntry = (entry: BodyMapPickerEntry) => {
    if (entry.groupId) {
      return (
        <BodyMapGroup
          entry={entry}
          expandedReportIds={expandedReportIds}
          key={entry.groupId}
          onToggleGroup={toggleMany}
          onToggleReportExpanded={toggleExpanded}
          onToggleReportSelected={toggleOne}
          selectedReportIds={localSelectedIds}
          testId={`${testId}-group-${entry.groupId}`}
        />
      )
    }

    return entry.reports.map((report) => (
      <BodyMapItem
        expanded={expandedReportIds.includes(report.id)}
        key={report.id}
        onToggleExpanded={toggleExpanded}
        onToggleSelected={toggleOne}
        report={report}
        selected={localSelectedIds.includes(report.id)}
        testId={`${testId}-entry-${report.id}`}
      />
    ))
  }

  const content = (
    <div data-testid={testId}>
      <Hint>
        <InfoCircleOutlined
          style={{ color: theme.color.neutral.color4, fontSize: 20 }}
        />
        <StyledText>
          {t(incidentReportsConstants.BODY_MAPS_GROUPED_HINT)}
        </StyledText>
      </Hint>

      {isLoading && (
        <Centred>
          <Spinner />
        </Centred>
      )}

      {/* react-query types error as unknown, so coerce before rendering. */}
      {!isLoading && Boolean(error) && (
        <Text level={1}>
          {t(incidentReportsConstants.BODY_MAPS_LOAD_FAILED)}
        </Text>
      )}

      {!isLoading && !error && entries.length === 0 && (
        <Text level={1}>
          {t(incidentReportsConstants.NO_BODY_MAPS_AVAILABLE)}
        </Text>
      )}

      {!isLoading && !error && entries.length > 0 && (
        <>
          <Entries>{visibleEntries.map(renderEntry)}</Entries>
          {hasMore && (
            <ShowMoreRow>
              <Button
                data-testid={`${testId}-show-more`}
                onClick={() =>
                  setVisibleCount(visibleReportCount + BODY_MAPS_PAGE_SIZE)
                }
                variant="text"
              >
                {t(incidentReportsConstants.SHOW_MORE_BODY_MAPS)}
              </Button>
            </ShowMoreRow>
          )}
        </>
      )}
    </div>
  )

  if (hasBackLink) {
    return (
      <DsDrawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
        title={t(incidentReportsConstants.ADDING_BODY_MAPS)}
        headerLeft={
          <DsButton
            variant="icon-only"
            aria-label={t(
              incidentReportsConstants.CASE_V2_INJURIES_BODY_MAPS_BACK
            )}
            data-testid={`${testId}-back`}
            icon={<ArrowLeftIcon />}
            onClick={onClose}
          />
        }
        closeLabel={t(incidentReportsConstants.CASE_V2_DRAWER_CLOSE)}
        actions={
          <>
            <DsButton
              data-testid={`${testId}-add`}
              onClick={() => onAdd(localSelectedIds)}
            >
              {t(incidentReportsConstants.ADD)}
            </DsButton>
            <DsButton
              variant="secondary"
              data-testid={`${testId}-cancel`}
              onClick={onClose}
            >
              {t(incidentReportsConstants.CANCEL)}
            </DsButton>
          </>
        }
      >
        {content}
      </DsDrawer>
    )
  }

  return (
    <StyledDrawer
      destroyOnClose
      footer={
        <Footer>
          <Button onClick={onClose} variant="default">
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button onClick={() => onAdd(localSelectedIds)} variant="primary">
            {t(incidentReportsConstants.ADD)}
          </Button>
        </Footer>
      }
      onClose={onClose}
      open={isOpen}
      placement="right"
      title={t(incidentReportsConstants.ADDING_BODY_MAPS)}
      width={DRAWER_WIDTH}
    >
      {content}
    </StyledDrawer>
  )
}

export default AddBodyMapsDrawer
